import { getAddressMetaKey, getContent } from '@/lib';
import type { DefaultInputState } from '@/store';
import type { ReactNode } from 'react';
import {
  filterOutNonApplicableAddressFields,
  validateForm,
} from '../../Form/utils';
import type { FormState } from '@/store/models/form/types';
import type { ResidentialAddress, SetAddressState } from './types';

const stripAddressPrefix = (id: string): string => {
  const addressRegex = /^(residential)(\w)/;
  return id.replace(addressRegex, (_, __, l) => l.toLowerCase());
};

/**
 * @function getFieldCurrentState
 * @param {object} params | An object
 * @param {string} params.field | field from formState
 * @param {string} params.addressType | billing or shipping address
 * @returns {object}
 */
const getFieldCurrentState = (
  {
    field,
    addressType,
  }: {
    field: string;
    addressType: string;
  },
  formState: any,
): DefaultInputState => {
  if (addressType) {
    return formState[`${addressType}Address`][field];
  }

  return formState[field];
};

/**
 * @function getFieldData
 * @param {object} target | event.target object
 * @param {object} meta | metadata object passed to change handler
 * @returns {object}
 */
export const getFieldData = (
  { id, name, type }: { id: string; name: string; type: string },
  { addressType }: { addressType: string },
  addressFormState: ResidentialAddress,
): { addressType: string; currentState: DefaultInputState; field: string } => {
  const isRadio = type === 'radio';
  const field = isRadio ? name : stripAddressPrefix(id);
  const currentState = getFieldCurrentState(
    { addressType, field },
    addressFormState,
  );

  return {
    addressType: addressType && `${addressType}Address`,
    currentState,
    field,
  };
};

export const getErrorMessageByAddressType = (
  addressType: string,
): ReactNode | null => {
  switch (addressType) {
    case 'P':
      return getContent('fieldErrors.primaryResidentialAddress.invalidPOBox');
    case 'R':
      return getContent('fieldErrors.primaryResidentialAddress.invalidRRBox');
    case 'billingAddress':
    case 'shippingAddress':
      return getContent(
        'fieldErrors.addressValidation.postOfficeBoxNotAllowed',
      );
    default:
      return null;
  }
};

export const validateAddressFields = (
  addressState: ResidentialAddress,
  setAddressState: SetAddressState,
): boolean => {
  const filteredAddressFields = filterOutNonApplicableAddressFields(
    addressState,
    getAddressMetaKey(),
  );
  const { formErrors } = validateForm({
    residentialAddress:
      filteredAddressFields as FormState['residentialAddress'],
  });
  const fieldsWithErrors = Object.entries(formErrors.residentialAddress || {});

  if (fieldsWithErrors.length > 0) {
    displayFormErrors(addressState, setAddressState, fieldsWithErrors);
    return false;
  }

  return true;
};

const displayFormErrors = (
  addressState: ResidentialAddress,
  setAddressState: SetAddressState,
  fieldsWithErrors: [string, unknown][],
) => {
  const errors = fieldsWithErrors.reduce((updatedState, [field, payload]) => {
    return {
      ...updatedState,
      [field]: {
        // @ts-expect-error TODO: typings
        ...addressState[field],
        // @ts-expect-error TODO: typings
        ...payload,
      },
    };
  }, {});

  setAddressState({
    ...addressState,
    ...errors,
  });
};
